'use client';

type Row = Record<string, any>;
type Change = { eventType: string; new: Row; old: Row };
type Binding = { event: string; table: string; filter?: string };
type Listener = { binding: Binding; callback: (change: Change) => void };
type Watch = { table: string; filter?: string; listeners: Set<Listener>; rows?: Map<string, Row>; timer?: ReturnType<typeof setTimeout>; stopped: boolean; refresh: () => void };
const watches = new Map<string, Watch>();
const deferred = new Set<Watch>();
let stream: EventSource | undefined;
let sessionVersion = 0;

function refreshAll() { watches.forEach((watch) => watch.refresh()); }
function onVisible() {
  if (document.hidden) return;
  deferred.forEach((watch) => watch.refresh());
  deferred.clear();
}
function connect() {
  if (stream) return;
  if (typeof EventSource === 'undefined') return;
  stream = new EventSource('/api/realtime');
  stream.addEventListener('ready', () => {
    refreshAll();
  });
  stream.addEventListener('change', (event) => {
    try {
      const { table, scopes } = JSON.parse((event as MessageEvent).data);
      watches.forEach((watch) => {
        if (watch.table !== table) return;
        const match = watch.filter?.match(/^([^=]+)=eq\.(.*)$/);
        if (match && Array.isArray(scopes?.[match[1]]) && !scopes[match[1]].includes(match[2])) return;
        watch.refresh();
      });
    } catch { /* Ignore malformed events. */ }
  });
  // EventSource retries the connection; do not restart database polling.
  stream.onerror = () => {};
  document.addEventListener('visibilitychange', onVisible);
}
function disconnect() {
  stream?.close(); stream = undefined;
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
}

export function resetRealtimeConnection() {
  sessionVersion++;
  disconnect();
  // Authentication changes must not compare another user's private snapshots.
  watches.forEach((watch) => { watch.rows = undefined; });
  deferred.clear();
  if (watches.size) connect();
}

function listen(listener: Listener) {
  const { table, filter } = listener.binding;
  const key = JSON.stringify([table, filter]);
  let watch = watches.get(key);
  if (!watch) {
    watch = { table, filter, listeners: new Set(), stopped: false, refresh: () => {} };
    watches.set(key, watch);
    const active = watch;
    let running = false;
    let dirty = false;
    const poll = async () => {
      if (active.stopped) return;
      if (running) { dirty = true; return; }
      running = true;
      const version = sessionVersion;
      try {
        if (typeof document !== 'undefined' && document.hidden && active.rows) { deferred.add(active); return; }
        const match = filter?.match(/^([^=]+)=eq\.(.*)$/);
        // Unsupported filters must never silently become unfiltered subscriptions.
        if (filter && !match) return;
        const response = await fetch('/api/local-db', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify({ table, action: 'select', filters: match ? [{ column: match[1], op: 'eq', value: match[2] }] : [] }),
        });
        if (!response.ok) return;
        const result = await response.json();
        if (active.stopped || version !== sessionVersion || !Array.isArray(result.data)) return;
        const next = new Map<string, Row>(result.data.map((row: Row) => [row.id, row]));
        const changes: Change[] = [];
        if (active.rows) {
          for (const [id, row] of next) {
            const old = active.rows.get(id);
            if (!old) changes.push({ eventType: 'INSERT', new: row, old: {} });
            else if (JSON.stringify(old) !== JSON.stringify(row)) changes.push({ eventType: 'UPDATE', new: row, old });
          }
          for (const [id, old] of active.rows) {
            if (!next.has(id)) changes.push({ eventType: 'DELETE', new: {}, old });
          }
        }
        active.rows = next;
        for (const change of changes) {
          for (const subscriber of active.listeners) {
            if (subscriber.binding.event === '*' || subscriber.binding.event === change.eventType) {
              try { subscriber.callback(change); } catch (error) { console.error('Live update handler failed', error); }
            }
          }
        }
      } catch {
        // Preserve the last snapshot on a connection failure and retry.
      } finally {
        running = false;
        if (dirty && !active.stopped) { dirty = false; active.refresh(); }
      }
    };
    active.refresh = () => {
      if (active.stopped) return;
      clearTimeout(active.timer);
      active.timer = setTimeout(poll, 100);
    };
    active.refresh();
  }
  watch.listeners.add(listener);
  connect();
  const active = watch;
  return () => {
    active.listeners.delete(listener);
    if (!active.listeners.size) {
      active.stopped = true;
      deferred.delete(active);
      clearTimeout(active.timer);
      watches.delete(key);
      if (!watches.size) disconnect();
    }
  };
}

export class LocalChannel {
  private listeners: Listener[] = [];
  private cleanup: Array<() => void> = [];
  on(type: string, binding: Binding, callback: (change: Change) => void) {
    if (type === 'postgres_changes') this.listeners.push({ binding, callback });
    return this;
  }
  subscribe() {
    if (!this.cleanup.length) this.cleanup = this.listeners.map(listen);
    return this;
  }
  unsubscribe() {
    this.cleanup.forEach((stop) => stop());
    this.cleanup = [];
  }
}
